import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarRange, CalendarClock, Repeat, Check, X } from "lucide-react";
import { STATUS_META, WEEKDAYS, todayISO, addDays } from "@/pages/hr/scheduleUtils";

export default function MySchedule() {
  const { can } = useAuth();
  const [emp, setEmp] = useState(null);
  const [err, setErr] = useState(null);
  const [start, setStart] = useState(todayISO());
  const [end, setEnd] = useState(addDays(todayISO(), 30));
  const [sched, setSched] = useState(null);
  const [offDay, setOffDay] = useState("");
  const [teammates, setTeammates] = useState([]);
  const [swapDate, setSwapDate] = useState("");
  const [swapTarget, setSwapTarget] = useState("");
  const [swaps, setSwaps] = useState([]);

  const loadSwaps = () => can("schedule:sunday_swap") && api.get("/hr/me/swaps").then(r => setSwaps(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/hr/me/employee").then(r => { setEmp(r.data); setOffDay(r.data.off_weekday || ""); })
      .catch(e => setErr(formatApiError(e.response?.data?.detail)));
    loadSwaps();
    // eslint-disable-next-line
  }, []);

  const generate = async () => {
    try { const { data } = await api.get(`/hr/me/schedule?start=${start}&end=${end}`); setSched(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveOff = async () => {
    if (!offDay) return;
    try { await api.post("/hr/me/off-day", { off_weekday: offDay }); toast.success("Off day updated"); if (sched) generate(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const requestSwap = async () => {
    if (!swapDate || !swapTarget) return toast.error("Pick a Sunday and a teammate");
    try { await api.post("/hr/me/sunday-swap", { sunday_date: swapDate, target_employee_id: swapTarget });
      toast.success("Swap requested"); setSwapDate(""); setSwapTarget(""); loadSwaps(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const respond = async (id, action) => {
    try { await api.post(`/hr/me/swaps/${id}/respond`, { action }); toast.success(`Swap ${action}ed`); loadSwaps(); if (sched) generate(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (err) return <div className="p-8 text-center text-slate-500" data-testid="myschedule-noemp">{err}</div>;

  return (
    <div data-testid="myschedule-page">
      <header className="mb-6">
        <div className="caption">My Schedule</div>
        <h1 className="font-heading text-3xl font-extrabold mt-1">{emp?.full_name || "My Schedule"}</h1>
        {emp && <p className="text-slate-500 mt-1 text-sm font-mono">{emp.employee_id}{emp.off_weekday ? ` · Weekly off: ${emp.off_weekday}` : ""}</p>}
      </header>

      <div className="grid grid-cols-[1fr_300px] gap-6">
        <div>
          <div className="flex items-end gap-2 mb-3 rounded-lg border border-slate-200 bg-white p-3">
            <div><Label>Start</Label><Input type="date" data-testid="my-start" value={start} onChange={e => setStart(e.target.value)} className="h-9" /></div>
            <div><Label>End</Label><Input type="date" data-testid="my-end" value={end} onChange={e => setEnd(e.target.value)} className="h-9" /></div>
            <Button onClick={generate} data-testid="my-generate" className="bg-sky-600 hover:bg-sky-700 h-9"><CalendarRange className="w-4 h-4 mr-1.5" />View</Button>
          </div>
          <div className="flex flex-wrap gap-2 mb-2 text-xs">
            {Object.entries(STATUS_META).map(([k, m]) => <span key={k} className={`px-2 py-0.5 rounded border ${m.cls}`}>{m.label}</span>)}
          </div>
          {sched && (
            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="my-schedule-list">
              {sched.days.map(d => {
                const e = d.entries[0] || {};
                const m = STATUS_META[e.status] || STATUS_META.working;
                return (
                  <div key={d.date} className="flex items-center justify-between px-3 py-2 border-b border-slate-50 last:border-0 text-sm">
                    <span className="font-mono">{d.date} <span className="uppercase text-xs text-slate-400">{d.weekday}</span></span>
                    <span className="flex items-center gap-2">
                      {e.work_start && <span className="text-xs text-slate-500">{e.work_start}–{e.work_end}</span>}
                      {e.note && <span className="text-xs text-rose-500">{e.note}</span>}
                      <Badge variant="outline" className={m.cls}>{m.label}</Badge>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="space-y-5">
          {can("schedule:select_off") && (
            <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="my-offday-panel">
              <h3 className="font-semibold flex items-center gap-2 mb-2"><CalendarClock className="w-4 h-4 text-sky-600" />Choose My Off Day</h3>
              <p className="text-xs text-slate-500 mb-2">Pick one weekly off day (Tue–Sat). Blocked dates are shown in your schedule.</p>
              <div className="flex gap-2">
                <Select value={offDay} onValueChange={setOffDay}>
                  <SelectTrigger data-testid="my-offday-select"><SelectValue placeholder="Select day" /></SelectTrigger>
                  <SelectContent>{WEEKDAYS.map(w => <SelectItem key={w.key} value={w.key}>{w.label}</SelectItem>)}</SelectContent>
                </Select>
                <Button onClick={saveOff} data-testid="my-offday-save" className="bg-slate-900">Save</Button>
              </div>
            </div>
          )}

          {can("schedule:sunday_swap") && (
            <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="my-swap-panel">
              <h3 className="font-semibold flex items-center gap-2 mb-2"><Repeat className="w-4 h-4 text-violet-600" />Sunday Off Swap</h3>
              <Label className="text-xs">Sunday date</Label>
              <Input type="date" value={swapDate} onChange={e => setSwapDate(e.target.value)} className="h-9 mb-2" data-testid="my-swap-date" />
              <Label className="text-xs">Swap with (FAS ID)</Label>
              <Input value={swapTarget} onChange={e => setSwapTarget(e.target.value)} placeholder="FAS002" className="h-9 mb-2" data-testid="my-swap-target" />
              <Button onClick={requestSwap} className="w-full bg-slate-900" data-testid="my-swap-request">Request Swap</Button>

              <div className="mt-3 space-y-1.5" data-testid="my-swaps-list">
                {swaps.map(s => (
                  <div key={s.id} className="flex items-center justify-between text-xs border-t border-slate-100 pt-1.5">
                    <span>{s.sunday_date}<br /><span className="text-slate-500">{s.requester_employee_id} → {s.target_employee_id}</span></span>
                    <span className="flex items-center gap-1">
                      <Badge variant="outline" className={s.status === "accepted" ? "text-emerald-700 border-emerald-300" : s.status === "rejected" ? "text-rose-700 border-rose-300" : "text-amber-700 border-amber-300"}>{s.status}</Badge>
                      {s.status === "pending" && s.target_employee_id === emp?.employee_id && <>
                        <button onClick={() => respond(s.id, "accept")} data-testid={`my-swap-accept-${s.id}`}><Check className="w-4 h-4 text-emerald-600" /></button>
                        <button onClick={() => respond(s.id, "reject")}><X className="w-4 h-4 text-rose-600" /></button>
                      </>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
